import type { CutsceneAudio, CutsceneSoundEffect, SoundChannel } from '@sorrem/shared';
import { getPageAudio } from '@sorrem-client/ui/audio/page-audio';

/** Clamps an authored volume into the 0..1 range `HTMLMediaElement` accepts. */
export function resolveCutsceneVolume(volume: number | undefined): number {
    return Math.min(1, Math.max(0, volume ?? 1));
}

/**
 * Plays a cutscene's music and its panels' sound effects. Owned by one
 * `CutscenePlayer` and disposed with it, so nothing keeps sounding after the
 * cutscene is skipped or finishes.
 *
 * Playback failures (autoplay blocked, missing file) are ignored: sound is an
 * extra, and a cutscene must still play silently. Music plays on the music
 * channel and effects on the effects channel, so the player's volume
 * settings apply (#260).
 */
export class CutsceneAudioPlayer {
    private music: HTMLAudioElement | null = null;
    private readonly panelSounds = new Set<HTMLAudioElement>();
    private readonly panelTimers = new Set<number>();
    /** Each playing element's way back out of the mixer. */
    private readonly disconnects = new Map<HTMLAudioElement, () => void>();

    /** Starts the cutscene's music; it loops unless authored otherwise. */
    playMusic(audio: CutsceneAudio): void {
        this.stopMusic();
        const element = new Audio(audio.src);
        element.loop = audio.isLooping ?? true;
        element.volume = resolveCutsceneVolume(audio.volume);
        this.music = element;
        this.route(element, 'music');
        void element.play().catch(() => undefined);
    }

    /** Schedules a panel's sound effects from now, replacing the previous panel's. */
    playPanelSfx(sfx: readonly CutsceneSoundEffect[]): void {
        this.stopPanelSfx();
        for (const effect of sfx) {
            const timerId = window.setTimeout(() => {
                this.panelTimers.delete(timerId);
                const element = new Audio(effect.src);
                element.loop = effect.isLooping ?? false;
                element.volume = resolveCutsceneVolume(effect.volume);
                element.addEventListener('ended', () => {
                    this.panelSounds.delete(element);
                    this.unroute(element);
                });
                this.panelSounds.add(element);
                this.route(element, 'effects');
                void element.play().catch(() => undefined);
            }, Math.max(0, effect.atMs));
            this.panelTimers.add(timerId);
        }
    }

    /**
     * Cancels sound effects that haven't fired yet and stops looping ones.
     * One-shot effects already playing are left to finish, so a quick
     * advance doesn't clip a sound mid-way.
     */
    stopPanelSfx(): void {
        for (const timerId of this.panelTimers) {
            window.clearTimeout(timerId);
        }
        this.panelTimers.clear();
        for (const element of this.panelSounds) {
            if (element.loop) {
                element.pause();
                this.panelSounds.delete(element);
                this.unroute(element);
            }
        }
    }

    /** Stops everything, including one-shot effects still playing. */
    dispose(): void {
        this.stopPanelSfx();
        for (const element of this.panelSounds) {
            element.pause();
            this.unroute(element);
        }
        this.panelSounds.clear();
        this.stopMusic();
    }

    private stopMusic(): void {
        if (this.music) {
            this.music.pause();
            this.unroute(this.music);
        }
        this.music = null;
    }

    private route(element: HTMLAudioElement, channel: SoundChannel): void {
        this.disconnects.set(element, getPageAudio().connectMediaElement(element, channel));
    }

    private unroute(element: HTMLAudioElement): void {
        this.disconnects.get(element)?.();
        this.disconnects.delete(element);
    }
}
